/**
 * PATH: packages/ui/src/components/settings/security-tab/TwoFactorSteps.tsx
 * PURPOSE: Presentational sub-components for each step of the 2FA setup/disable flow.
 * WHY: Extracted from TwoFactorSection to keep each file under 300 lines.
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../lib/utils (cn): classname merge
 */

import {
  Shield,
  ShieldCheck,
  Smartphone,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Eye,
  EyeOff,
} from 'lucide-react'
import { cn } from '../../../lib/utils'

/* ───── Shared code input ───── */

function CodeInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      placeholder="000000"
      className="flex-1 px-4 py-2 rounded-lg border border-border bg-background text-center font-mono text-lg tracking-widest"
      maxLength={6}
    />
  )
}

/* ───── Step: idle + not enabled ───── */

export function SetupPrompt({
  isLoading,
  onStart,
}: {
  isLoading: boolean
  onStart: () => void
}) {
  return (
    <div className="bg-muted/30 rounded-xl p-6 border border-border">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-primary/10">
          <Smartphone className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium mb-1">Authenticator App</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Use an authenticator app like Google Authenticator, Authy, or 1Password to generate verification codes.
          </p>
          <button
            onClick={onStart}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Enable 2FA
          </button>
        </div>
      </div>
    </div>
  )
}

/* ───── Step: QR code scan + verify ───── */

export function QrCodeStep({
  qrCode,
  secret,
  showSecret,
  onToggleSecret,
  verificationCode,
  onCodeChange,
  onConfirm,
  isLoading,
  error,
  onCancel,
}: {
  qrCode: string
  secret: string
  showSecret: boolean
  onToggleSecret: () => void
  verificationCode: string
  onCodeChange: (v: string) => void
  onConfirm: () => void
  isLoading: boolean
  error: string
  onCancel: () => void
}) {
  return (
    <div className="bg-muted/30 rounded-xl p-6 border border-border space-y-6">
      <div className="text-center">
        <h4 className="font-medium mb-2">Scan QR Code</h4>
        <p className="text-sm text-muted-foreground mb-4">Scan this QR code with your authenticator app</p>
        {qrCode && (
          <div className="inline-block p-4 bg-white rounded-xl mb-4">
            <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
          </div>
        )}
        <div className="text-left max-w-sm mx-auto">
          <p className="text-xs text-muted-foreground mb-2">Or enter this code manually:</p>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted font-mono text-sm">
            <span className={cn(!showSecret && 'blur-sm select-none')}>{secret}</span>
            <button onClick={onToggleSecret} className="p-1 rounded hover:bg-muted-foreground/10">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <p className="text-sm font-medium mb-2">Enter verification code</p>
        <div className="flex gap-2">
          <CodeInput value={verificationCode} onChange={onCodeChange} />
          <button
            onClick={onConfirm}
            disabled={isLoading || verificationCode.length !== 6}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
          </button>
        </div>
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </div>
      <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground">
        Cancel setup
      </button>
    </div>
  )
}

/* ───── Step: display backup codes ───── */

export function BackupCodesDisplay({
  backupCodes,
  copied,
  onCopy,
  onFinish,
}: {
  backupCodes: string[]
  copied: boolean
  onCopy: () => void
  onFinish: () => void
}) {
  return (
    <div className="bg-muted/30 rounded-xl p-6 border border-border space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-600 dark:text-amber-400">Save your backup codes</p>
          <p className="text-muted-foreground">
            Store these codes somewhere safe. You can use them to access your account if you lose your phone.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-muted font-mono text-sm">
        {backupCodes.map((code, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-muted-foreground w-4">{i + 1}.</span>
            <span>{code}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button
          onClick={onCopy}
          className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted flex items-center justify-center gap-2"
        >
          {copied ? (
            <><Check className="w-4 h-4 text-green-500" /> Copied!</>
          ) : (
            <><Copy className="w-4 h-4" /> Copy Codes</>
          )}
        </button>
        <button
          onClick={onFinish}
          className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90"
        >
          Done
        </button>
      </div>
    </div>
  )
}

/* ───── Step: 2FA is enabled ───── */

export function EnabledStatus({
  backupCodesRemaining,
  onDisable,
  onNewBackupCodes,
}: {
  backupCodesRemaining: number
  onDisable: () => void
  onNewBackupCodes: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 rounded-xl bg-green-500/10 border border-green-500/20">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-green-500" />
          <div>
            <p className="font-medium text-green-600 dark:text-green-400">2FA is enabled</p>
            <p className="text-sm text-muted-foreground">
              {backupCodesRemaining > 0 ? `${backupCodesRemaining} backup codes remaining` : 'Using authenticator app'}
            </p>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onDisable}
          className="px-4 py-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
        >
          Disable 2FA
        </button>
        <button
          onClick={onNewBackupCodes}
          className="px-4 py-2 rounded-lg border border-border hover:bg-muted flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          New Backup Codes
        </button>
      </div>
    </div>
  )
}

/* ───── Step: disable confirmation ───── */

export function DisableConfirmation({
  verificationCode,
  onCodeChange,
  onDisable,
  isLoading,
  error,
  onCancel,
}: {
  verificationCode: string
  onCodeChange: (v: string) => void
  onDisable: () => void
  isLoading: boolean
  error: string
  onCancel: () => void
}) {
  return (
    <div className="bg-destructive/10 rounded-xl p-6 border border-destructive/20 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
        <div>
          <p className="font-medium text-destructive">Disable Two-Factor Authentication?</p>
          <p className="text-sm text-muted-foreground">
            This will make your account less secure. Enter your verification code to confirm.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <CodeInput value={verificationCode} onChange={onCodeChange} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">
          Cancel
        </button>
        <button
          onClick={onDisable}
          disabled={isLoading || verificationCode.length !== 6}
          className="flex-1 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground font-medium hover:bg-destructive/90 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
        </button>
      </div>
    </div>
  )
}
